import { useMemo, useState } from "react";
import {
  Avatar, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, Drawer, IconButton, InputAdornment, LinearProgress, List, ListItemButton,
  ListItemIcon, ListItemText, Stack, TextField, ThemeProvider, Tooltip,
  Typography, useMediaQuery, useTheme,
} from "@mui/material";
import AddRounded from "@mui/icons-material/AddRounded";
import ArrowBackRounded from "@mui/icons-material/ArrowBackRounded";
import ArticleOutlined from "@mui/icons-material/ArticleOutlined";
import AccountTreeOutlined from "@mui/icons-material/AccountTreeOutlined";
import AccountBalanceWalletOutlined from "@mui/icons-material/AccountBalanceWalletOutlined";
import ChevronRightRounded from "@mui/icons-material/ChevronRightRounded";
import DarkModeOutlined from "@mui/icons-material/DarkModeOutlined";
import FolderOutlined from "@mui/icons-material/FolderOutlined";
import LightModeOutlined from "@mui/icons-material/LightModeOutlined";
import MenuRounded from "@mui/icons-material/MenuRounded";
import SearchRounded from "@mui/icons-material/SearchRounded";
import SettingsOutlined from "@mui/icons-material/SettingsOutlined";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { getStoredThemeMode, PORTAL_THEME_MODE_STORAGE_KEY } from "../../components/theme/theme";
import { getAipoIdentity } from "./access";
import { AipoProvider, useAipo } from "./AipoContext";
import { demoCredits, formatCredits } from "./mockData";
import { createAipoTheme } from "./theme";

const SIDEBAR_WIDTH = 286;
const sidebarTheme = createAipoTheme("dark");

const navItems = [
  { label: "Projetos", to: "/aipo/projetos", icon: FolderOutlined },
  { label: "Artefatos", to: "/aipo/artefatos", icon: ArticleOutlined },
  { label: "Conhecimento", to: "/aipo/conhecimento", icon: AccountTreeOutlined },
  { label: "Conexões", to: "/aipo/conexoes", icon: SettingsOutlined },
  { label: "Assinatura e créditos", to: "/aipo/assinatura", icon: AccountBalanceWalletOutlined },
];

const pageTitle = (pathname, projects, chats) => {
  if (pathname === "/aipo" || pathname === "/aipo/") return "Início";
  if (pathname === "/aipo/novo") return "Nova conversa";
  if (pathname.startsWith("/aipo/chats/")) return chats.find((item) => pathname.endsWith(`/${item.id}`))?.title || "Conversa";
  if (pathname.startsWith("/aipo/projetos/")) return projects.find((item) => pathname.endsWith(`/${item.id}`))?.name || "Projeto";
  if (pathname.startsWith("/aipo/artefatos/")) return "Artefato";
  return navItems.find((item) => item.to === pathname)?.label || "AIPO";
};

const AipoWorkspace = ({ mode, onToggleMode }) => {
  const { projects, chats, createProject, identity } = useAipo();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [projectDialogOpen, setProjectDialogOpen] = useState(false);
  const [projectName, setProjectName] = useState("");
  const [projectDescription, setProjectDescription] = useState("");
  const query = search.trim().toLocaleLowerCase("pt-BR");
  const matchingProjects = projects.filter((project) => project.name.toLocaleLowerCase("pt-BR").includes(query));
  const matchingChats = chats.filter((chat) => chat.title.toLocaleLowerCase("pt-BR").includes(query)).slice(0, 6);

  const closeMobile = () => setMobileOpen(false);
  const submitProject = (event) => {
    event.preventDefault();
    if (!projectName.trim()) return;
    const id = createProject(projectName, projectDescription);
    setProjectName("");
    setProjectDescription("");
    setProjectDialogOpen(false);
    closeMobile();
    navigate(`/aipo/projetos/${id}`);
  };

  const navItemSx = {
    borderRadius: 1.2,
    minHeight: 38,
    px: 1.25,
    color: "text.secondary",
    "& .MuiListItemIcon-root": { color: "inherit", minWidth: 34 },
    "&:hover": { bgcolor: "action.hover", color: "text.primary" },
    "&.active": { bgcolor: "rgba(139,92,246,0.14)", color: "primary.main", fontWeight: 700 },
  };

  const sidebar = (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", bgcolor: mode === "dark" ? "#0d0f14" : "#27242b", color: "text.primary", borderRight: "1px solid", borderColor: "divider" }}>
      <Stack
        component={NavLink}
        to="/aipo"
        onClick={closeMobile}
        direction="row"
        alignItems="center"
        aria-label="Ir para a página inicial do AIPO"
        justifyContent="center"
        sx={{ px: 2, py: 2, color: "inherit", textDecoration: "none", "&:hover": { bgcolor: "action.hover" }, "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: -2 } }}
      >
        <Box sx={{ minWidth: 0, textAlign: "center" }}>
          <Typography sx={{ fontWeight: 800, fontSize: 18, lineHeight: 1.1 }}>{"{AIPO}"}</Typography>
          <Typography variant="caption" color="text.secondary">por Aipim.tech</Typography>
        </Box>
      </Stack>

      <Box sx={{ px: 1.5, pb: 1.4 }}>
        <Button component={NavLink} to="/home_logado" onClick={closeMobile} fullWidth startIcon={<ArrowBackRounded />} variant="outlined" color="inherit" sx={{ justifyContent: "flex-start", color: "text.secondary", borderColor: "divider", mb: 1.4 }}>
          Voltar ao portal
        </Button>
        <TextField size="small" fullWidth placeholder="Buscar projetos e chats" value={search} onChange={(event) => setSearch(event.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><SearchRounded fontSize="small" /></InputAdornment> }} inputProps={{ "aria-label": "Buscar projetos e chats" }} />
        <Button component={NavLink} to="/aipo/novo" onClick={closeMobile} fullWidth variant="contained" startIcon={<AddRounded />} sx={{ mt: 1.4, justifyContent: "flex-start", py: 1.1 }}>
          Novo chat
        </Button>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: 1.5, pb: 2 }}>
        {!query && (
          <List disablePadding sx={{ mb: 2 }}>
            {navItems.map(({ label, to, icon: Icon }) => (
              <ListItemButton key={to} component={NavLink} to={to} onClick={closeMobile} sx={navItemSx}>
                <ListItemIcon><Icon fontSize="small" /></ListItemIcon>
                <ListItemText primary={label} primaryTypographyProps={{ fontSize: 13.5, fontWeight: 600 }} />
              </ListItemButton>
            ))}
          </List>
        )}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1.2, mb: 0.5 }}>
          <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 800, letterSpacing: 1.1 }}>Projetos</Typography>
          <Tooltip title="Criar projeto"><IconButton size="small" onClick={() => setProjectDialogOpen(true)} aria-label="Criar projeto"><AddRounded fontSize="small" /></IconButton></Tooltip>
        </Stack>
        {matchingProjects.map((project) => (
          <ListItemButton key={project.id} component={NavLink} to={`/aipo/projetos/${project.id}`} onClick={closeMobile} sx={navItemSx}>
            <ListItemIcon><FolderOutlined fontSize="small" /></ListItemIcon>
            <ListItemText primary={project.name} primaryTypographyProps={{ noWrap: true, fontSize: 13.5 }} />
          </ListItemButton>
        ))}
        {query && !matchingProjects.length && !matchingChats.length && <Typography variant="body2" color="text.secondary" sx={{ px: 1.2, py: 2 }}>Nenhum resultado encontrado.</Typography>}
        <Typography variant="overline" color="text.secondary" sx={{ display: "block", px: 1.2, mt: 2, mb: 0.5, fontWeight: 800, letterSpacing: 1.1 }}>Conversas recentes</Typography>
        {matchingChats.map((chat) => (
          <ListItemButton key={chat.id} component={NavLink} to={`/aipo/chats/${chat.id}`} onClick={closeMobile} sx={navItemSx}>
            <ListItemIcon><ChevronRightRounded fontSize="small" /></ListItemIcon>
            <ListItemText primary={chat.title} primaryTypographyProps={{ noWrap: true, fontSize: 13.5 }} />
          </ListItemButton>
        ))}
      </Box>
      <Divider />
      <Box
        component={NavLink}
        to="/aipo/assinatura"
        onClick={closeMobile}
        sx={{ display: "block", mx: 1.5, my: 1.5, p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 1.2, color: "text.primary", textDecoration: "none", bgcolor: "background.paper", "&:hover": { borderColor: "primary.main" } }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="caption" color="text.secondary" fontWeight={700}>Créditos disponíveis</Typography>
          <AccountBalanceWalletOutlined color="primary" fontSize="small" />
        </Stack>
        <Typography sx={{ mt: 0.6, mb: 1.2, fontSize: 23, lineHeight: 1.1, fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>
          {formatCredits(demoCredits.includedAvailable + demoCredits.additionalAvailable)}
        </Typography>
        <LinearProgress variant="determinate" value={(demoCredits.includedUsed / demoCredits.includedTotal) * 100} sx={{ height: 5, borderRadius: 3 }} />
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.7 }}>Saldo ilustrativo · {formatCredits(demoCredits.includedUsed)} usados</Typography>
      </Box>
      <Divider />
      <Stack direction="row" alignItems="center" spacing={1.2} sx={{ p: 1.7 }}>
        <Avatar sx={{ width: 32, height: 32, bgcolor: "primary.dark", fontSize: 13 }}>{identity.name.slice(0, 1).toUpperCase()}</Avatar>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="body2" fontWeight={700} noWrap>{identity.name}</Typography>
          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>{identity.company || identity.email || "Cliente Aipim"}</Typography>
        </Box>
      </Stack>
    </Box>
  );

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default", color: "text.primary", display: "flex", fontFamily: "\"Segoe UI\", sans-serif" }}>
      <ThemeProvider theme={sidebarTheme}>
        {isDesktop ? <Box component="aside" sx={{ width: SIDEBAR_WIDTH, flexShrink: 0, height: "100vh", position: "sticky", top: 0 }}>{sidebar}</Box> : <Drawer open={mobileOpen} onClose={closeMobile} PaperProps={{ sx: { width: SIDEBAR_WIDTH, bgcolor: "background.default" } }}>{sidebar}</Drawer>}
      </ThemeProvider>
      <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        <Box component="header" sx={{ minHeight: 65, px: { xs: 2, md: 3.5 }, display: "flex", alignItems: "center", gap: 1.5, borderBottom: "1px solid", borderColor: "divider", bgcolor: mode === "dark" ? "#101218" : "#d7d3dc" }}>
          {!isDesktop && <IconButton onClick={() => setMobileOpen(true)} aria-label="Abrir menu"><MenuRounded /></IconButton>}
          <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1, minWidth: 0 }} noWrap>{pageTitle(location.pathname, projects, chats)}</Typography>
          <Tooltip title="Saldo ilustrativo; o saldo real ainda não está conectado">
            <Button component={NavLink} to="/aipo/assinatura" size="small" color="inherit" startIcon={<AccountBalanceWalletOutlined fontSize="small" />} sx={{ whiteSpace: "nowrap", minWidth: 0 }}>
              <Box sx={{ display: { xs: "none", sm: "inline" } }}>{formatCredits(demoCredits.includedAvailable + demoCredits.additionalAvailable)} créditos</Box>
              <Box sx={{ display: { xs: "inline", sm: "none" } }}>{formatCredits(demoCredits.includedAvailable + demoCredits.additionalAvailable)}</Box>
            </Button>
          </Tooltip>
          <Tooltip title={mode === "dark" ? "Ativar modo claro" : "Ativar modo escuro"}>
            <IconButton onClick={onToggleMode} aria-label={mode === "dark" ? "Ativar modo claro" : "Ativar modo escuro"}>{mode === "dark" ? <LightModeOutlined /> : <DarkModeOutlined />}</IconButton>
          </Tooltip>
        </Box>
        <Box component="main" sx={{ flex: 1, width: "100%", minWidth: 0 }}>
          <Box sx={{ maxWidth: 1180, mx: "auto", p: { xs: 2, sm: 3, lg: 4 } }}>
            <Outlet context={{ openProjectDialog: () => setProjectDialogOpen(true) }} />
          </Box>
        </Box>
      </Box>

      <Dialog open={projectDialogOpen} onClose={() => setProjectDialogOpen(false)} fullWidth maxWidth="sm">
        <Box component="form" onSubmit={submitProject}>
          <DialogTitle>Criar projeto</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Organize conversas sobre uma operação, empreendimento ou tema.</Typography>
            <TextField autoFocus fullWidth label="Nome do projeto" value={projectName} onChange={(event) => setProjectName(event.target.value)} inputProps={{ maxLength: 80 }} sx={{ mb: 2 }} />
            <TextField fullWidth label="Descrição (opcional)" value={projectDescription} onChange={(event) => setProjectDescription(event.target.value)} multiline minRows={2} />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}><Button onClick={() => setProjectDialogOpen(false)} color="inherit">Cancelar</Button><Button type="submit" variant="contained" disabled={!projectName.trim()}>Criar projeto</Button></DialogActions>
        </Box>
      </Dialog>
    </Box>
  );
};

const AipoApp = () => {
  const mode = getStoredThemeMode();
  const theme = useMemo(() => createAipoTheme(mode), [mode]);
  const identity = getAipoIdentity();
  const toggleMode = () => {
    const next = mode === "dark" ? "light" : "dark";
    localStorage.setItem(PORTAL_THEME_MODE_STORAGE_KEY, next);
    window.location.reload();
  };

  return (
    <ThemeProvider theme={theme}>
      <AipoProvider key={`${identity.clientId}:${identity.email}`} identity={identity}>
        <AipoWorkspace mode={mode} onToggleMode={toggleMode} />
      </AipoProvider>
    </ThemeProvider>
  );
};

export default AipoApp;
